// src/restaurant/pages/RestaurantLogin.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebase';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../components/GoogleMapsProvider';
import './RestaurantLogin.css';

const RestaurantLogin = () => {
  const { isLoaded } = useGoogleMaps();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurantData, setRestaurantData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [restaurantLocation, setRestaurantLocation] = useState({
    lat: 29.3759, // موقع افتراضي في الكويت
    lng: 47.9774
  });
  const [accurateLocation, setAccurateLocation] = useState(false);
  const navigate = useNavigate();
  const functions = getFunctions();

  // تبديل عرض كلمة السر
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // محاولة الحصول على الموقع الحالي عند تحميل الصفحة
  useEffect(() => {
    if (navigator.geolocation && showLocationPicker) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setRestaurantLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting current location:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [showLocationPicker]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('الرجاء إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // تسجيل الدخول باستخدام Firebase Authentication
      await signInWithEmailAndPassword(auth, email, password);
      // التحقق من أن المستخدم هو مطعم من خلال البحث في Firestore
      const restaurantsRef = collection(db, 'restaurants');
      const q = query(restaurantsRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // المستخدم ليس مطعماً
        await auth.signOut(); // تسجيل الخروج
        setError('هذا الحساب غير مسجل كمطعم. الرجاء التواصل مع الإدارة.');
        setLoading(false);
        return;
      }

      // استخراج بيانات المطعم
      const restaurantDoc = querySnapshot.docs[0];
      const resId = restaurantDoc.id;
      const resData = restaurantDoc.data();
      
      setRestaurantId(resId);
      setRestaurantData(resData);
      
      // نطلب تحديث الموقع في كل مرة يسجل فيها المطعم الدخول للحصول على موقع دقيق
      setShowLocationPicker(true);
      if (resData.location && resData.location.lat && resData.location.lng) {
        setRestaurantLocation({
          lat: resData.location.lat,
          lng: resData.location.lng
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('خطأ في تسجيل الدخول:', error);
      setError('خطأ في تسجيل الدخول. الرجاء التحقق من البريد الإلكتروني وكلمة المرور.');
      setLoading(false);
    }
  };

  const completeLogin = (resId, restaurantData) => {
    localStorage.setItem('restaurantId', resId);
    localStorage.setItem('restaurantName', restaurantData.name);
    localStorage.setItem('restaurantEmail', restaurantData.email);
    localStorage.setItem('userRole', 'restaurant');
    
    setLoading(false);
    navigate('/restaurant/dashboard');
  };

  const handleMapClick = (e) => {
    if (!e.latLng) return;
    
    setRestaurantLocation({
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    });
    setAccurateLocation(true);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setRestaurantLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setAccurateLocation(true);
          setLoading(false);
        },
        (error) => {
          console.error("Error getting current location:", error);
          setError("فشل في تحديد الموقع الحالي. يرجى محاولة تحديد الموقع يدوياً على الخريطة.");
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setError("متصفحك لا يدعم تحديد الموقع. يرجى محاولة تحديد الموقع يدوياً على الخريطة.");
    }
  };

  const saveRestaurantLocation = async () => {
    if (!restaurantId) return;
    
    setLoading(true);
    
    try {
      // استخدام دالة Firebase الجديدة لتحديث موقع المطعم
      const updateRestaurantLocation = httpsCallable(functions, 'updateRestaurantLocation');
      const result = await updateRestaurantLocation({ 
        location: restaurantLocation,
        updateSource: 'login' 
      });

      console.log('✅ تم تحديث موقع المطعم بنجاح:', result.data);
      
      // تحديث بيانات المطعم المحلية
      const updatedRestaurantData = {
        ...restaurantData,
        location: restaurantLocation
      };
      
      completeLogin(restaurantId, updatedRestaurantData);
    } catch (error) {
      console.error('❌ فشل تحديث موقع المطعم:', error);
      
      // في حال فشل الدالة السحابية، نحاول التحديث مباشرة
      try {
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        await updateDoc(restaurantRef, {
          location: restaurantLocation,
          locationUpdatedAt: new Date()
        });
        
        console.log('✅ تم تحديث موقع المطعم كاحتياط:', restaurantLocation);
        
        // تحديث بيانات المطعم المحلية
        const updatedRestaurantData = {
          ...restaurantData,
          location: restaurantLocation
        };
        
        completeLogin(restaurantId, updatedRestaurantData);
      } catch (backupError) {
        console.error('❌ فشل التحديث الاحتياطي لموقع المطعم:', backupError);
        setError('حدث خطأ أثناء حفظ موقع المطعم. يرجى المحاولة مرة أخرى.');
        setLoading(false);
      }
    }
  };

  // عرض نموذج تسجيل الدخول أو شاشة تحديد الموقع
  if (showLocationPicker) {
    return (
      <div className="restaurant-login-container">
        <div className="restaurant-login-card location-picker-container">
          <div className="restaurant-login-header">
            <h2>تأكيد موقع المطعم</h2>
            <p>يرجى التأكد من دقة موقع المطعم لضمان وصول الطلبات بشكل صحيح</p>
          </div>

          {error && <div className="restaurant-login-error">{error}</div>}

          {isLoaded ? (
            <div className="map-container" style={{ height: '300px', marginBottom: '20px' }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={restaurantLocation}
                zoom={17}
                onClick={handleMapClick}
                options={{
                  streetViewControl: true,
                  fullscreenControl: true,
                  mapTypeControl: true
                }}
              >
                <Marker 
                  position={restaurantLocation} 
                  draggable={true}
                  onDragEnd={(e) => {
                    setRestaurantLocation({
                      lat: e.latLng.lat(),
                      lng: e.latLng.lng()
                    });
                    setAccurateLocation(true);
                  }}
                />
              </GoogleMap>
            </div>
          ) : (
            <div className="map-loading" style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0' }}>
              جاري تحميل الخريطة...
            </div>
          )}

          <div className="location-coordinates" style={{ textAlign: 'center', margin: '15px 0', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <p>الإحداثيات: {restaurantLocation.lat.toFixed(6)}, {restaurantLocation.lng.toFixed(6)}</p>
            {accurateLocation && <p className="accurate-location">✅ تم تحديد الموقع بدقة</p>}
          </div>

          <button 
            className="get-location-button" 
            onClick={getCurrentLocation}
            style={{ width: '100%', marginBottom: '10px', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            disabled={loading}
          >
            📍 الحصول على الموقع الحالي بدقة
          </button>

          <div className="location-actions" style={{ display: 'flex', gap: '10px' }}>
            <button
              className="restaurant-login-button"
              onClick={saveRestaurantLocation}
              disabled={loading}
              style={{ flex: 3 }}
            >
              {loading ? 'جاري الحفظ...' : 'تأكيد موقع المطعم والدخول'}
            </button>
            <button
              className="restaurant-login-button"
              onClick={() => setShowLocationPicker(false)}
              disabled={loading}
              style={{ flex: 1, backgroundColor: '#6c757d' }}
            >
              رجوع
            </button>
          </div>
        </div>
      </div>
    );
  }

  // عرض نموذج تسجيل الدخول
  return (
    <div className="restaurant-login-container">
      <div className="restaurant-login-card">
        <div className="restaurant-login-header">
          <h2>تسجيل دخول المطعم</h2>
          <p>أدخل بيانات الدخول الخاصة بالمطعم</p>
        </div>

        {error && <div className="restaurant-login-error">{error}</div>}

        <form onSubmit={handleLogin} className="restaurant-login-form">
          <div className="form-group">
            <label htmlFor="email">البريد الإلكتروني</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="أدخل البريد الإلكتروني"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">كلمة المرور</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="أدخل كلمة المرور"
                disabled={loading}
                required
                style={{ backgroundColor: '#f9f9f9' }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 1
                }}
                onClick={togglePasswordVisibility}
                title={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
              >
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#777" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  {showPassword ? (
                    // Eye icon with slash (hidden password)
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    // Eye icon (visible password)
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </div>
            </div>
          </div>

          <button type="submit" className="restaurant-login-button" disabled={loading}>
            {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
          </button>
        </form>

        <div className="restaurant-login-footer">
          <button 
            className="forgot-password-link"
            onClick={() => navigate('/restaurant/forgot-password')}
            style={{ background: 'none', border: 'none', color: '#4a90e2', cursor: 'pointer' }}
          >
            نسيت كلمة المرور؟
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantLogin;